import React, { useEffect, useState, useCallback } from 'react';
import {
  Button,
  InputAdornment,
  IconButton,
  Paper,
  Grid,
  Box,
  TextField,
  Typography
} from '@mui/material';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useForgetPassword, useResetPassword } from 'hooks/useUserAuth';
import { snackbarService } from 'services/snackbarService';
import { useLocation, useNavigate } from 'react-router-dom';
import useGlobalStyles from 'styles/globalStyles';
import { colors } from 'styles/colors';
import CustomButton from 'components/buttons/CustomButton/CustomButton';

const RESEND_TIME = 59;

const ResetPasswordPage = () => {
  const globalStyles = useGlobalStyles();
  const location = useLocation();
  const navigate = useNavigate();

  const [email] = useState(() => location.state?.email);
  const [codeSent, setCodeSent] = useState(false);
  const [timer, setTimer] = useState(RESEND_TIME);
  const [values, setValues] = useState({
    password: '',
    showPassword: false
  });

  const resetSchema = yup.object().shape({
    new_password: yup
      .string()
      .required('Password is required')
      .min(8, "New Password must be at least 8 characters."),
    verification_code: yup.string().required('Verification code is required')
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm({
    resolver: yupResolver(resetSchema)
  });

  const { mutate: resetMutation, isLoading } = useResetPassword(
    (res: any) => {
      if (res.status_code === 200) {
        snackbarService.showSnackbar(
          'Password has been changed successfully',
          'success'
        );
        navigate('/signin');
      } else {
        snackbarService.showSnackbar('Invalid verification code', 'error');
      }
    },
    () => { }
  );

  const { mutate: forgotMutation } = useForgetPassword(
    (res: any) => {
      if (res.status_code === 200) {
        snackbarService.showSnackbar('Verification code sent', 'success');
      }
    },
    () => { }
  );

  /** ---------------- TIMER LOGIC ---------------- */
  useEffect(() => {
    if (!codeSent || timer === 0) return;

    const interval = setInterval(() => {
      setTimer(prev => prev - 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [codeSent, timer]);

  useEffect(() => {
    if (timer === 0) {
      setCodeSent(false);
      setTimer(RESEND_TIME);
    }
  }, [timer]);

  /** ---------------- HANDLERS ---------------- */
  const onSubmit = (values: any) => {
    resetMutation({
      email,
      ...values
    });
  };

  const resendVerifyLink = useCallback(() => {
    if (codeSent) return;

    forgotMutation(email);
    setCodeSent(true);
    setTimer(RESEND_TIME);
  }, [codeSent, email, forgotMutation]);

  const handleClickShowPassword = () => {
    setValues(prev => ({ ...prev, showPassword: !prev.showPassword }));
  };

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    event.preventDefault();
  };

  /** ---------------- RENDER ---------------- */
  return (
    <Box>
      <Grid container direction="row" className={globalStyles.boxSignIn}>
        <Grid
          size={{ xs: 12, sm: 8, md: 5 }}
          className={globalStyles.bigBox}>
          <Paper variant="outlined" className={globalStyles.paperSignIn}>
            <Box mb={3}>
              <Typography variant="subtitle1">Reset Password</Typography>
            </Box>

            <form onSubmit={handleSubmit(onSubmit)}>
              <Typography variant="body2" className="forgetPasswordHeader" sx={{ color: colors.secondary }}>
                Please enter the code we sent to your email address{' '}
                <span className="email" style={{ color: colors.warning }}>“{email}”</span>
              </Typography>

              <TextField
                {...register('verification_code')}
                label="Verification code"
                margin="normal"
                fullWidth
                variant="standard"
                error={!!errors.verification_code}
                helperText={errors.verification_code?.message}
              />

              <TextField
                {...register('new_password')}
                label="New Password"
                type={values.showPassword ? 'text' : 'password'}
                fullWidth
                variant="standard"
                error={!!errors.new_password}
                helperText={errors.new_password?.message}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={handleClickShowPassword}
                          onMouseDown={handleMouseDownPassword}
                        >
                          {values.showPassword ? (
                            <VisibilityOff />
                          ) : (
                            <Visibility />
                          )}
                        </IconButton>
                      </InputAdornment>
                    )
                  }
                }}
              />

              <Box mt={3} display="flex" flexDirection="column">
                <CustomButton 
                  text='Reset password'
                  type='submit'
                  fullWidth
                  disabled={!isValid}
                  loading={isLoading}
                />

                <Box mt={2}>
                  <a
                    className="resendLink"
                    onClick={resendVerifyLink}
                    style={{
                      pointerEvents: codeSent ? 'none' : 'auto',
                      opacity: codeSent ? 0.6 : 1,
                      cursor: 'pointer'
                    }}
                  >
                    {codeSent
                      ? `You can resend code in ${timer}s`
                      : 'Re-send verification code'}
                  </a>
                </Box>
              </Box>
            </form>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ResetPasswordPage;
